<template>
  <div class="help-search" role="search">
    <label :for="inputId" class="visually-hidden">Search help</label>
    <div class="help-search__field">
      <i class="fas fa-search" aria-hidden="true" />
      <input
        :id="inputId"
        ref="input"
        v-model="query"
        type="search"
        class="help-search__input"
        :placeholder="placeholder"
        autocomplete="off"
        :aria-describedby="`${inputId}-status`"
        @focus="ensureIndex"
        @input="ensureIndex"
        @keydown.down.prevent="focusResult(0)"
      >
    </div>
    <p :id="`${inputId}-status`" class="visually-hidden" aria-live="polite">{{ statusText }}</p>
    <div v-if="query.trim().length >= 2" class="help-search__results">
      <p v-if="loading" class="help-search__state">Loading guides…</p>
      <p v-else-if="error" class="help-search__state">{{ error }}</p>
      <ul v-else-if="results.length" ref="list" class="help-search__list">
        <li v-for="(result, index) in results" :key="result.slug">
          <router-link
            :to="`/help/${result.slug}`"
            class="help-search__result"
            @click="$emit('navigate')"
            @keydown.down.prevent="focusResult(index + 1)"
            @keydown.up.prevent="index === 0 ? $refs.input.focus() : focusResult(index - 1)"
          >
            <span class="help-search__title">{{ result.title }}</span>
            <span class="help-search__meta">{{ result.section_title }} · {{ result.type_label }}</span>
            <span class="help-search__summary">{{ result.summary }}</span>
          </router-link>
        </li>
      </ul>
      <div v-else class="help-search__state">
        <p class="mb-1">No guides match “{{ query.trim() }}”.</p>
        <p class="mb-0">
          Try other words, or
          <router-link to="/help/get-started/getting-help" @click="$emit('navigate')">contact support</router-link>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { fetchHelpSearchIndex } from '@/utils/helpApi'
import { buildHelpSearch, searchHelp } from '@/utils/helpSearch'

let uid = 0

export default {
  name: 'HelpSearch',
  props: {
    placeholder: { type: String, default: 'Search the guides, e.g. “webhook signature” or “add a product”' },
    autofocus: { type: Boolean, default: false },
  },
  emits: ['navigate'],
  data() {
    uid += 1
    return { inputId: `help-search-${uid}`, query: '', index: null, loading: false, error: '' }
  },
  computed: {
    ...mapGetters(['currentUser']),
    results() {
      return searchHelp(this.index, this.query)
    },
    statusText() {
      if (this.query.trim().length < 2 || this.loading) return ''
      return this.results.length ? `${this.results.length} guides found` : 'No guides found'
    },
  },
  mounted() {
    if (this.autofocus) this.$nextTick(() => this.$refs.input?.focus())
  },
  methods: {
    async ensureIndex() {
      if (this.index || this.loading) return
      this.loading = true
      this.error = ''
      try {
        const data = await fetchHelpSearchIndex(this.currentUser)
        this.index = buildHelpSearch(data.articles)
      } catch (e) {
        this.error = e?.message || 'Could not load the guides.'
      } finally {
        this.loading = false
      }
    },
    focusResult(index) {
      const links = this.$refs.list?.querySelectorAll('a') || []
      if (links[index]) links[index].focus()
    },
  },
}
</script>

<style scoped>
.help-search__field {
  align-items: center;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  display: flex;
  gap: 0.6rem;
  padding: 0 0.85rem;
}
.help-search__field:focus-within {
  border-color: var(--clinch-blue-primary, #33b5ff);
  box-shadow: 0 0 0 3px rgba(51, 181, 255, 0.2);
}
.help-search__field i { color: var(--muted-foreground); }
.help-search__input {
  background: transparent;
  border: 0;
  color: var(--foreground);
  flex: 1;
  font-size: 1rem;
  min-height: 46px;
  outline: none;
}
.help-search__results {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  margin-top: 0.5rem;
  max-height: 26rem;
  overflow-y: auto;
}
.help-search__list { list-style: none; margin: 0; padding: 0.25rem; }
.help-search__result {
  border-radius: 8px;
  color: var(--foreground);
  display: block;
  padding: 0.6rem 0.75rem;
  text-decoration: none;
}
.help-search__result:hover,
.help-search__result:focus-visible { background: var(--muted); outline: none; }
.help-search__title { display: block; font-weight: 700; }
.help-search__meta { color: var(--muted-foreground); display: block; font-size: 0.78rem; }
.help-search__summary { color: var(--muted-foreground); display: block; font-size: 0.875rem; margin-top: 0.15rem; }
.help-search__state { color: var(--muted-foreground); font-size: 0.9rem; margin: 0; padding: 0.85rem 1rem; }
</style>
